import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from '@bbpos/ui';

// FR-AUTH-020 — log the user out after SESSION_TIMEOUT_MINUTES without any
// interaction, and warn them first so unsaved work is not lost silently. The
// API enforces the same timeout on its own (the session key slides with each
// request), so this is the courtesy in front of it, not the control itself.
export const IDLE_TIMEOUT_MS =
  (Number(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES) || 30) * 60_000;
export const IDLE_WARNING_MS = 2 * 60_000;
/** While the user is active but making no API calls (reading, typing a long
 *  form), ping this often so the server's session slides with them. */
export const KEEP_ALIVE_MS = 5 * 60_000;
const TICK_MS = 1_000;
/** Shared by every admin tab: activity in one keeps the others signed in too
 *  (they share one server session, so one tab idling out would end it for all). */
const ACTIVITY_KEY = 'bbpos_last_activity';
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

function readSharedActivity(): number {
  try {
    return Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeSharedActivity(at: number): void {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(at));
  } catch {
    // storage blocked — this tab still tracks its own activity
  }
}

function formatRemaining(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

interface IdleSessionGuardProps {
  /** Ends the session (logout + go to the login screen). */
  onExpire: () => void;
  /** Any authenticated request — slides the server-side session. */
  keepAlive: () => Promise<unknown>;
}

export function IdleSessionGuard({ onExpire, keepAlive }: IdleSessionGuardProps) {
  const lastActivity = useRef(Date.now());
  const lastPing = useRef(Date.now());
  const lastShared = useRef(0);
  const warningOpen = useRef(false);
  const expired = useRef(false);
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    const onActivity = () => {
      // Once the warning is up only its button extends the session — a stray
      // mouse move must not dismiss it behind the user's back.
      if (warningOpen.current) return;
      const now = Date.now();
      lastActivity.current = now;
      if (now - lastShared.current > 5_000) {
        lastShared.current = now;
        writeSharedActivity(now);
      }
    };
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, onActivity, { passive: true });
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onActivity);
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      if (expired.current) return;
      const now = Date.now();
      const last = Math.max(lastActivity.current, readSharedActivity());
      const idle = now - last;
      if (idle >= IDLE_TIMEOUT_MS) {
        expired.current = true;
        warningOpen.current = false;
        setRemaining(null);
        onExpire();
        return;
      }
      if (idle >= IDLE_TIMEOUT_MS - IDLE_WARNING_MS) {
        warningOpen.current = true;
        setRemaining(IDLE_TIMEOUT_MS - idle);
        return;
      }
      if (warningOpen.current) {
        // Another tab saw activity — the warning no longer applies here.
        warningOpen.current = false;
        setRemaining(null);
      }
      if (last > lastPing.current && now - lastPing.current >= KEEP_ALIVE_MS) {
        lastPing.current = now;
        keepAlive().catch(() => {
          // A rejected session is handled by the api-client (redirect to login).
        });
      }
    };
    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, [onExpire, keepAlive]);

  const staySignedIn = useCallback(() => {
    const now = Date.now();
    lastActivity.current = now;
    lastPing.current = now;
    lastShared.current = now;
    writeSharedActivity(now);
    warningOpen.current = false;
    setRemaining(null);
    keepAlive().catch(() => {
      // handled by the api-client
    });
  }, [keepAlive]);

  const signOutNow = useCallback(() => {
    expired.current = true;
    warningOpen.current = false;
    setRemaining(null);
    onExpire();
  }, [onExpire]);

  return (
    <Modal
      open={remaining !== null}
      onClose={staySignedIn}
      title="Your session is about to expire"
      closeOnBackdrop={false}
      showCloseButton={false}
      footer={
        <>
          <Button variant="ghost" onClick={signOutNow}>
            Sign out now
          </Button>
          <Button variant="primary" onClick={staySignedIn}>
            Stay signed in
          </Button>
        </>
      }
    >
      <div className="flex items-center gap-4 animate-fade-in">
        <div className="w-14 h-14 shrink-0 rounded-full bg-gradient-to-br from-warning-500 to-danger-500 flex items-center justify-center text-white shadow-md">
          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            You have been inactive for a while. For your security you will be signed out in
          </p>
          <p
            className="text-2xl font-bold tabular-nums text-danger-600 dark:text-danger-400"
            aria-live="polite"
          >
            {formatRemaining(remaining ?? 0)}
          </p>
        </div>
      </div>
    </Modal>
  );
}
